<template>
  <div class="main">
    <FormWrapper :action="formAction" :method="formMethod">
      <div class="title-bar">
        <h1 class="mb-0">
          {{ learningPath.id ? "Edit" : "Add New" }} Learning Path
        </h1>
        <a class="btn-back" :href="route('admin.learning-paths.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Back to List</span>
        </a>
      </div>

      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9">
          <div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
            </div>

            <div class="col-12">
              <EditorGroupToggle
                label="Learning Path Settings"
                target="toggleGroupSettings"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupSettings" class="collapse show row">
                <div class="col-12 col-xxl-6 mb-3">
                  <Field
                    type="text"
                    name="name"
                    label="Name"
                    :value="learningPath.name"
                    validate="required"
                  />
                </div>
                <div class="col-12 col-xxl-6 mb-3">
                  <Field
                    type="text"
                    name="length"
                    label="Subtitle"
                    :value="learningPath['length']"
                    validate="required"
                  />
                </div>
                <div class="col-12 col-xl-6 col-xxl-3 mb-3">
                  <Field
                    type="text"
                    name="expiry_days"
                    label="Expires (Days)"
                    :value="learningPath.expiry_days"
                  />
                </div>
                <div class="col-12 col-xl-6 col-xxl-3 mb-3">
                  <Field
                    type="text"
                    name="discount"
                    label="Discount Amount"
                    :value="learningPath.discount"
                    help-text="% of the subtotal of all included courses"
                  />
                </div>
                <div class="col-12 col-xl-6 col-xxl-3 mb-3">
                  <Field
                    type="text"
                    name="nate_cost"
                    label="NATE Cost"
                    :value="learningPath.nate_cost"
                    help-text="The cost for NATE credit if all included courses are free"
                  />
                </div>
                <div class="d-none d-xl-flex col-xl-6 col-xxl-3"></div>
                <div class="col-12 col-xl-6 mb-3">
                  <Field
                    type="select"
                    name="owner_type"
                    label="Ownership Type"
                    v-model="ownerType"
                    :options="ownerTypes"
                    :hideSelectEmptyOption="true"
                  />
                </div>
                <div class="col-12 col-xl-6 mb-3">
                  <div class="owned-by-wrap">
                    <icon
                      v-if="showOwnedByLoadingIcon"
                      icon="loading"
                      class="loading-icon"
                    />
                    <Field
                      v-if="ownedByOptions.length > 0"
                      type="select"
                      name="owned_by"
                      :label="ownedByLabel"
                      v-model="ownedBy"
                      :options="ownedByOptions"
                      :hideSelectEmptyOption="true"
                    />
                  </div>
                </div>
                <div v-if="isDistributorOwned" class="col-12 mb-3">
                  <Field
                    type="checkbox"
                    name="hide_from_factory"
                    id="cb_hide_from_factory"
                    label="Hide Path from Factory Users"
                    :value="!!learningPath.hide_from_factory"
                    :checkbox-value="1"
                  />
                </div>
                <div class="col-12 mb-3">
                  <Field
                    type="text"
                    name="keywords"
                    label="Keywords"
                    :value="learningPath.keywords"
                  />
                </div>
                <div class="col-12 col-xl-6 mb-3 pt-3">
                  <Field
                    type="checkbox"
                    name="managers_only"
                    id="cb_managers_only"
                    label="Only distributor and factory users may enroll in this learning path"
                    :value="!!learningPath.managers_only"
                    :checkbox-value="1"
                  />
                </div>
                <div class="col-12 col-xl-6 mb-3 pt-3">
                  <Field
                    type="checkbox"
                    name="fad_only"
                    id="cb_fad_only"
                    label="Only FAD\Elite dealers may enroll in this learning path"
                    :value="!!learningPath.fad_only"
                    :checkbox-value="1"
                  />
                </div>
                <div class="col-12 col-xl-6 mb-3 pt-3">
                  <Field
                      type="checkbox"
                      name="factory_only"
                      id="cb_fatory_only"
                      label="Only Factory users may enroll in this learning path"
                      :value="!!learningPath.factory_only"
                      :checkbox-value="1"
                  />
                </div>
                <div class="col-12 col-xl-6 mb-3 pt-3">
                  <Field
                      type="checkbox"
                      name="is_featured"
                      id="cb_is_featured"
                      label="Feature this learning path above other listings"
                      :value="!!learningPath.is_featured"
                      :checkbox-value="1"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Required Courses, Brands &amp; Tags"
                target="toggleGroupFilters"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupFilters" class="collapse show row no-gutters">
                <div class="col-12 mb-3">
                  <SuggestMultiField
                    label="Required Courses"
                    name="courses"
                    search-input-name="required_course_name"
                    search-input-text="Search by course name"
                    :search-url="route('admin.courses.search.online')"
                    :value="learningPath._courses"
                  />
                </div>
                <div class="col-12 mb-3 col-xl-6 pr-xl-3">
                  <MultiAssignField
                    type="select"
                    label="Brand"
                    name="brands"
                    :options="brands"
                    :value="learningPath._brands"
                  />
                </div>
                <div class="col-12 mb-3 col-xl-6 pl-xl-3">
                  <MultiAssignField
                    type="select"
                    label="Tags"
                    name="tags"
                    :options="tags"
                    :value="learningPath._tags"
                  />
                </div>
                <div class="col-12 mb-3 col-xl-6 pr-xl-3">
                    <MultiAssignField
                        type="select"
                        label="Categories"
                        name="categories"
                        :options="categories"
                        :value="learningPath._categories"
                    />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Surveys &amp; Tests"
                target="toggleGroupForms"
                class="mb-4"
                :collapsed="false"
              />
              <div id="toggleGroupForms" class="collapse show row no-gutters">
                <div class="col-12 mb-3 col-xl-6 pr-xl-3">
                  <Field
                    type="select"
                    label="Survey"
                    name="survey_id"
                    :options="surveys"
                    :value="learningPath.survey_id"
                  />
                </div>
                <div class="col-12 mb-3 col-xl-6 pl-xl-3">
                  <Field
                    type="select"
                    label="Test"
                    name="test_id"
                    :options="tests"
                    :value="learningPath.test_id"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Suggested Content"
                target="toggleGroupSuggestions"
                class="mb-4"
                :collapsed="false"
              />
              <div
                id="toggleGroupSuggestions"
                class="collapse show row no-gutters"
              >
                <div class="col-12 mb-3">
                  <h4 class="text-primary text-uppercase mb-3">
                    Suggested Videos
                  </h4>
                  <SuggestMultiField
                    name="videos"
                    search-input-name="suggested_video_name"
                    search-input-text="Search by video title"
                    :search-url="route('admin.videos.search')"
                    :value="learningPath._videos"
                  />
                </div>
                <div class="col-12 mb-3">
                  <h4 class="text-primary text-uppercase mb-3">
                    Suggested Courses
                  </h4>
                  <SuggestMultiField
                    name="suggested_courses"
                    search-input-name="required_course_name"
                    search-input-text="Search by course name or ID"
                    :search-url="route('admin.courses.search')"
                    :value="learningPath._suggested_courses"
                  />
                </div>
                <div class="col-12">
                  <Field
                    type="checkbox"
                    name="meta[hide_suggestions_with_no_events]"
                    id="cb_hide_suggestions_with_no_events"
                    label="Auto-hide classroom suggestions with no upcoming events"
                    :value="!!learningPath.hide_suggestions_with_no_events"
                    :checkbox-value="1"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Additional Content"
                target="toggleGroupContent"
                class="mb-4"
              />
              <div id="toggleGroupContent" class="collapse row no-gutters">
                <div class="col-12 mb-3">
                  <h4 class="text-primary text-uppercase">Description</h4>
                  <HtmlField
                    name="description"
                    :value="learningPath.description"
                  />
                </div>
                <div class="col-12 mb-3 col-xl-6 pr-xl-3">
                  <h4 class="text-primary text-uppercase mb-3">
                    Featured Image
                  </h4>
                  <AssetField
                    name="image_id"
                    :value="learningPath.image_id"
                    :types="['image']"
                  />
                </div>
              </div>
            </div>

            <div class="col-12">
              <hr />
              <EditorGroupToggle
                label="Administrative Notes & Policies"
                target="toggleGroupNotes"
                class="mb-4"
              />
              <div id="toggleGroupNotes" class="collapse row no-gutters">
                <div class="col-12 mb-3">
                  <HtmlField
                    name="meta[admin_notes]"
                    label="Admin Notes"
                    :value="learningPath.admin_notes"
                    :hideAssetIcons="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <div class="col-12">
                <h4 class="text-uppercase text-center">Status</h4>
                <SwitchField
                  name="is_active"
                  on-label="Active"
                  off-label="Inactive"
                  :value="!!learningPath.is_active ? 1 : 0"
                />
                <Field
                  type="checkbox"
                  class="mt-3"
                  name="disable_emails"
                  id="cb_disable_emails"
                  label="Disable notifications for this learning path"
                  :value="!!learningPath.disable_emails"
                  :checkbox-value="1"
                />
                <hr class="w-75" />
              </div>
              <div class="col-12 pb-3">
                <button type="submit" class="btn btn-success w-50">
                  Save Changes
                </button>
              </div>
              <div class="col-12 pb-3">
                <a
                  :href="route('admin.learning-paths.index')"
                  class="btn btn-danger w-50"
                  >Cancel & Exit</a
                >
              </div>
              <template v-if="learningPath.id">
                <div class="col-12 pb-3">
                  <a
                    :href="route('admin.learning-paths.show', routeParam)"
                    class="btn btn-info w-50"
                    >Preview</a
                  >
                </div>
                <div class="col-12 pb-3">
                  <a
                    :href="
                      route(
                        'admin.learning-paths.enrollments.index',
                        routeParam
                      )
                    "
                    class="btn btn-info w-50"
                    >View Enrollments</a
                  >
                </div>
                <div class="col-12 font-size-sm text-center pt-4">
                  <span
                    >Last updated on
                    {{ learningPath.updated_at | moment("MM/DD/YYYY") }}</span
                  >
                  <span
                    >at {{ learningPath.updated_at | moment("h:mma") }}</span
                  >
                  <span v-if="learningPath.updated_by">{{
                    " " + learningPath.updated_by.name
                  }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
export default {
  mixins: [DisplaysAlerts],
  props: {
    learningPath: { type: Object, required: true },
    ownerTypes: { type: Array, required: true },
    brands: { type: Array, default: () => [] },
    categories: { type: Array, default: () => []},
    tags: { type: Array, default: () => [] },
    tests: { type: Array, default: () => [] },
    surveys: { type: Array, default: () => [] },
  },
  data() {
    return {
      ownerType: null,
      ownedBy: null,
      distributorOwners: [],
      trainerOwners: [],
      showOwnedByLoadingIcon: false,
    };
  },
  computed: {
    routeParam() {
      return { learning_path: this.learningPath.id || null };
    },
    formAction() {
      return !this.learningPath.id
        ? this.route("admin.learning-paths.store")
        : this.route("admin.learning-paths.update", this.routeParam);
    },
    formMethod() {
      return !this.learningPath.id ? "POST" : "PUT";
    },
    ownedByOptions() {
      if (this.ownerType == "DISTRIBUTOR") {
        return this.distributorOwners;
      } else if (this.ownerType == "TRAINER") {
        return this.trainerOwners;
      } else {
        return [];
      }
    },
    ownedByLabel() {
      if (this.ownerType == "DISTRIBUTOR") {
        return "Distributor Company";
      } else if (this.ownerType == "TRAINER") {
        return "Trainer Company";
      } else {
        return "";
      }
    },
    isDistributorOwned() {
      return this.ownerType === "DISTRIBUTOR";
    },
  },
  watch: {
    ownerType(val) {
      if (val == "DISTRIBUTOR" && this.distributorOwners.length == 0) {
        this.loadDistributorOwners();
      } else if (val == "TRAINER" && this.trainerOwners.length == 0) {
        this.loadTrainerOwners();
      } else {
        this.showOwnedByLoadingIcon = false;
      }
    },
  },
  methods: {
    loadDistributorOwners() {
      this.showOwnedByLoadingIcon = true;
      this.axios
        .get(this.route("admin.courses.owners"), {
          params: { owner_type: "DISTRIBUTOR" },
        })
        .then((response) => {
          this.distributorOwners = response.data;
          this.showOwnedByLoadingIcon = false;
        });
    },
    loadTrainerOwners() {
      this.showOwnedByLoadingIcon = true;
      this.axios
        .get(this.route("admin.courses.owners"), {
          params: { owner_type: "TRAINER" },
        })
        .then((response) => {
          this.trainerOwners = response.data;
          this.showOwnedByLoadingIcon = false;
        });
    },
  },
  mounted() {
    this.ownerType = this.learningPath.owner_type;
    this.ownedBy = this.learningPath.owned_by;
  },
};
</script>

<style lang="scss" scoped>
.owned-by-wrap {
  .loading-icon {
    position: absolute;
    top: 38px;
    z-index: 2;
  }
}
</style>